import type { ScrollAreaProps, ScrollAreaShadow, ScrollAreaShadowPlacement, ScrollBarProps } from './const';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { cn } from '@utils';
import * as React from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './index.less';

const ALL_SHADOW_PLACEMENTS: ScrollAreaShadowPlacement[] = ['top', 'bottom', 'left', 'right'];

function getScrollShadowPlacements(scrollShadow?: ScrollAreaShadow) {
  if (!scrollShadow) {
    return [];
  }

  if (scrollShadow === true) {
    return ALL_SHADOW_PLACEMENTS;
  }

  return Array.isArray(scrollShadow) ? scrollShadow : [scrollShadow];
}

function shouldShowScrollShadow(
  element: HTMLElement,
  placement: ScrollAreaShadowPlacement,
  threshold: number,
) {
  const { scrollTop, scrollHeight, clientHeight } = element;
  const { scrollLeft, scrollWidth, clientWidth } = element;

  switch (placement) {
    case 'top':
      return scrollTop > threshold;
    case 'bottom':
      return scrollHeight - scrollTop - clientHeight > threshold;
    case 'left':
      return scrollLeft > threshold;
    case 'right':
      return scrollWidth - scrollLeft - clientWidth > threshold;
    default:
      return false;
  }
}

function isSamePlacementSet(
  prev: Set<ScrollAreaShadowPlacement>,
  next: Set<ScrollAreaShadowPlacement>,
) {
  if (prev.size !== next.size) {
    return false;
  }

  for (const item of next) {
    if (!prev.has(item)) {
      return false;
    }
  }

  return true;
}

function assignRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T) => {
    refs.forEach(currentRef => {
      if (!currentRef) {
        return;
      }

      if (typeof currentRef === 'function') {
        currentRef(node);
        return;
      }

      currentRef.current = node;
    });
  };
}

function ScrollBar(props: ScrollBarProps, ref: React.Ref<React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>>) {
  const { className, orientation = 'vertical', style, thumbStyle, variant = 'dark', ...rest } = props;

  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      ref={ref}
      orientation={orientation}
      className={cn(
        'wg-scroll-area__scrollbar',
        orientation === 'vertical' && 'wg-scroll-area__scrollbar--vertical',
        orientation === 'horizontal' && 'wg-scroll-area__scrollbar--horizontal',
        variant === 'light' && 'wg-scroll-area__scrollbar--light',
        variant === 'dark' && 'wg-scroll-area__scrollbar--dark',
        className,
      )}
      style={style}
      {...rest}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        style={thumbStyle}
        className={cn(
          'wg-scroll-area__thumb',
          variant === 'light' && 'wg-scroll-area__thumb--light',
          variant === 'dark' && 'wg-scroll-area__thumb--dark',
        )}
        data-vaul-no-drag
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
}

const ScrollBarComponent = forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>, ScrollBarProps>(ScrollBar);

ScrollBarComponent.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

function ScrollArea(props: ScrollAreaProps, ref: React.Ref<React.ElementRef<typeof ScrollAreaPrimitive.Viewport>>) {
  const {
    className,
    children,
    viewportClassName,
    viewportId,
    viewportStyle,
    scrollClassName,
    scrollStyle,
    scrollRef,
    thumbStyle,
    variant = 'dark',
    scrollShadow,
    scrollShadowThreshold = 8,
    onScroll,
    ...rest
  } = props;
  const viewportRef = useRef<React.ElementRef<typeof ScrollAreaPrimitive.Viewport> | null>(null);
  const shadowPlacements = useMemo(() => getScrollShadowPlacements(scrollShadow), [scrollShadow]);
  const [visibleShadowPlacements, setVisibleShadowPlacements] = useState<Set<ScrollAreaShadowPlacement>>(new Set());

  const updateShadowVisibility = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || shadowPlacements.length === 0) {
      setVisibleShadowPlacements(prev => (prev.size === 0 ? prev : new Set()));
      return;
    }

    const nextVisiblePlacements = new Set(
      shadowPlacements.filter(item => shouldShowScrollShadow(viewport, item, scrollShadowThreshold)),
    );

    setVisibleShadowPlacements(prev => (
      isSamePlacementSet(prev, nextVisiblePlacements) ? prev : nextVisiblePlacements
    ));
  }, [shadowPlacements, scrollShadowThreshold]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || shadowPlacements.length === 0) {
      updateShadowVisibility();
      return;
    }

    updateShadowVisibility();

    const resizeObserver = new ResizeObserver(updateShadowVisibility);
    resizeObserver.observe(viewport);
    if (viewport.firstElementChild) {
      resizeObserver.observe(viewport.firstElementChild);
    }

    return () => resizeObserver.disconnect();
  }, [shadowPlacements, updateShadowVisibility]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    updateShadowVisibility();
    onScroll?.(e);
  };

  return (
    <ScrollAreaPrimitive.Root
      className={cn('wg-scroll-area', className)}
      {...rest}
    >
      <ScrollAreaPrimitive.Viewport
        className={cn('wg-scroll-area__viewport', viewportClassName)}
        ref={assignRefs(viewportRef, ref)}
        style={viewportStyle}
        id={viewportId}
        onScroll={handleScroll}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {shadowPlacements.map(placement => (
        <div
          key={placement}
          aria-hidden
          className={cn(
            'wg-scroll-area__shadow',
            `wg-scroll-area__shadow--${placement}`,
            visibleShadowPlacements.has(placement) && 'wg-scroll-area__shadow--visible',
          )}
        />
      ))}
      <ScrollBarComponent
        className={scrollClassName}
        thumbStyle={thumbStyle}
        style={scrollStyle}
        ref={scrollRef}
        variant={variant}
      />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

const ScrollAreaComponent = forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.Viewport>, ScrollAreaProps>(ScrollArea);

ScrollAreaComponent.displayName = ScrollAreaPrimitive.Root.displayName;

export default ScrollAreaComponent;
export { ScrollBarComponent as ScrollBar };
export type { ScrollAreaProps, ScrollBarProps };
